import { formatDate } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { ROUTER_NAVIGATED, ROUTER_NAVIGATION, RouterNavigationAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import ObjectId from 'bson-objectid';
import {
  catchError,
  concatMap,
  debounceTime,
  delay,
  distinctUntilChanged,
  exhaustMap,
  filter,
  forkJoin,
  map,
  of,
  pairwise,
  switchMap,
  tap,
} from 'rxjs';

import {
  IntervalCode,
  IssuesListView,
  IssueSortField,
  Item,
  LinkedItemTypeEnum,
  OrdinalOrUserOrdinalUpdate,
  ratingSortStartDirection,
  SortDirection,
} from '@ninety/platform/models';
import { Comment as IssueComment } from '@ninety/platform/models/_shared/comment';
import { DetailType } from '@ninety/platform/models/_shared/detail-type.enum';
import { MeetingType } from '@ninety/platform/models/meetings/meeting-type.enum';
import {
  AuxiliaryRouterOutletService,
  ExcelExportType,
  FilterService,
  FilterServiceActions,
} from '@ninety/platform/services';
import {
  DetailViewActions,
  FeatureFlagKeys,
  LayoutsIssuesEvents,
  MazSharedActions,
  MazToolName,
  MeetingIssuesEvents,
  selectFeatureFlag,
  selectIssueRatingUsesLabels,
  selectShowIssuesTrash,
  SpinnerActions,
  NotificationActions,
  selectCompanyId,
  selectCompanySltTeamId,
  selectIsInMeeting,
  TeamListStateActions,
  TeamSelectors,
  UserListStateActions,
  FilterUserSelectors,
  UserSettingsActions,
  selectCurrentUser,
  selectCurrentUserId,
  selectLanguageArea,
  teamAll,
  teamNone,
  CurrentUserSelectors,
  appActions,
} from '@ninety/platform/state';
import * as currentUserSelectors from '@ninety/platform/state/app-entities/users/users-state.selectors';
import {
  getLocalPageSize,
  PaginationConstants,
  ConfirmDialogComponent,
  ConfirmDialogData,
  WarningConfirmDialogComponent,
  PrintDialogComponent,
} from '@ninety/ui/core';
import { FileService } from '@ninety/ui/legacy/core/services/file.service';
import { LibraryService } from '@ninety/ui/legacy/core/services/library.service';
import { PrintApi, PrintService } from '@ninety/ui/legacy/core/services/print.service';
import { DiscardChangesFlow } from '@ninety/web/pages/detail-view/_services/discard-changes.flow';
import { UniversalCreateActions } from '@ninety/web/pages/layouts/_state/universal-create/universal-create.actions';
import { VtoStateActions } from '@ninety/web/pages/vto/_state/vto-state.actions';

import { Issue } from '../_shared/models/issue.model';
import { IssuesLocalStorageService } from '../_shared/services/issues-local-storage.service';
import { IssuesV2Service, SearchIssuesParams } from '../_shared/services/issues-v2.service';
import { IssueListSettingsDialogComponent } from '../components/issues-list-settings-dialog/issues-list-settings-dialog.component';
import {
  SendToTeamDialogComponent,
  SendToTeamDialogData,
  SendToTeamDialogResult,
} from '../components/send-to-team-dialog/send-to-team-dialog.component';

import { IssuesArchivePageActions } from './archive/archive.actions';
import { IssuesActions } from './issues.actions';
import { LoadingState } from './issues.model';
import * as IssuesSelectors from './issues.selectors';
import { INLINE_TEMP_ISSUE_ID } from './issues.selectors';

@Injectable()
export class IssuesEffects {
  private readonly actions$ = inject(Actions);
  private readonly discardChangesFlow = inject(DiscardChangesFlow);
  private readonly store = inject(Store);
  private readonly filterService = inject(FilterService);
  private readonly issuesV2Service = inject(IssuesV2Service);
  private readonly fileService = inject(FileService);
  private readonly issuesLocalStorageService = inject(IssuesLocalStorageService);
  private readonly dialog = inject(MatDialog);
  private readonly printService = inject(PrintService);
  private readonly auxiliaryRouterOutletService = inject(AuxiliaryRouterOutletService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly libraryService = inject(LibraryService);
  private readonly router = inject(Router);

  readonly ISSUES_V2_ROUTE_PATH = 'issues';

  static readonly DEBOUNCE_TIME_MS = 400;

  // CRUD Effects
  createIssue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.createIssue),
      switchMap(({ issue }) =>
        this.issuesV2Service.createIssue(issue).pipe(
          map(createdIssue => IssuesActions.createIssueSuccess({ issue: createdIssue })),
          catchError((error: unknown) => of(IssuesActions.createIssueFailure({ error, issue })))
        )
      )
    )
  );

  /**
   * Sync attachment events from LibraryService for UC and other flows that bypass FileAttachmentsComponent.
   * Card component uses (attachmentAdded)/(attachmentRemoved) outputs instead.
   * Dedupe if both paths fire for the same attachment id.
   */
  syncAttachmentEvents$ = createEffect(() =>
    this.libraryService.attachmentEvent$.pipe(
      filter(({ attachment }) => attachment.parentType === 'Issue'),
      concatLatestFrom(event => this.store.select(IssuesSelectors.selectIssueById(event.attachment.parentId))),
      filter(([event, issue]) => {
        if (!issue) return false;
        const attachmentExists = issue.attachments?.some(a => a._id === event.attachment._id) ?? false;
        // Upload: only dispatch if NOT already present; Remove: only dispatch if present
        return event.type === 'upload' ? !attachmentExists : attachmentExists;
      }),
      map(([event]) => {
        const issueId = event.attachment.parentId;
        return event.type === 'upload'
          ? IssuesActions.addAttachment({ issueId, attachment: event.attachment })
          : IssuesActions.removeAttachment({ issueId, attachment: event.attachment });
      })
    )
  );

  updateIssue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.updateIssue),
      switchMap(({ issueId, changes }) =>
        this.issuesV2Service.updateIssue(issueId, changes).pipe(
          map(updatedIssue => IssuesActions.updateIssueSuccess({ issue: updatedIssue, changes })),
          catchError((error: unknown) => of(IssuesActions.updateIssueFailure({ error, issueId, changes })))
        )
      )
    )
  );

  // concatMap: switchMap would cancel A's in-flight PATCH when B is completed, and mergeMap would
  // race complete → uncomplete on the same issue.
  setCompletedState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.setCompletedState),
      concatMap(({ issueId, completed }) =>
        this.issuesV2Service.updateIssue(issueId, { completed }).pipe(
          map(updatedIssue => IssuesActions.setCompletedStateSuccess({ issue: updatedIssue })),
          catchError((error: unknown) => of(IssuesActions.setCompletedStateFailure({ error, issueId, completed })))
        )
      )
    )
  );

  completeAndArchiveIssue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.completeAndArchiveIssue),
      switchMap(({ issueId }) =>
        this.issuesV2Service.updateIssue(issueId, { completed: true, archived: true, archivedDate: new Date() }).pipe(
          map(() => IssuesActions.completeAndArchiveIssueSuccess({ issueId })),
          catchError(() => of(IssuesActions.completeAndArchiveIssueFailure()))
        )
      )
    )
  );

  setCompletedStateMeetingEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.setCompletedStateSuccess),
      map(({ issue }) => MeetingIssuesEvents.setCompletedState({ issueId: issue._id, completed: issue.completed }))
    )
  );

  issueCreatedForMetricsMeetingEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.createIssueSuccess, IssuesActions.saveInlineSuccess),
      map(({ issue }) => MeetingIssuesEvents.created({ intervalCode: issue.intervalCode, teamId: issue.teamId }))
    )
  );

  issueDeletedMeetingEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.deleteIssueSuccess),
      concatLatestFrom(() => this.store.select(IssuesSelectors.selectIsShortTermIssues)),
      map(([{ issueId }, isShortTerm]) => MeetingIssuesEvents.deleted({ issueId, isShortTerm }))
    )
  );

  issueTeamChangedMeetingEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.updateTeamSuccess),
      concatLatestFrom(() => this.store.select(IssuesSelectors.selectIsShortTermIssues)),
      map(([{ issueId, teamId }, isShortTerm]) => MeetingIssuesEvents.teamChanged({ issueId, teamId, isShortTerm }))
    )
  );

  issueIntervalChangedMeetingEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.setIssueIntervalSuccess),
      map(({ issue, intervalCode }) => MeetingIssuesEvents.intervalChanged({ issueId: issue._id, intervalCode }))
    )
  );

  issueArchivedMeetingEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.archiveIssueSuccess, IssuesActions.completeAndArchiveIssueSuccess),
      concatLatestFrom(() => this.store.select(IssuesSelectors.selectIsShortTermIssues)),
      map(([action, isShortTerm]) => {
        const completed = 'completed' in action ? !!action.completed : true;
        return MeetingIssuesEvents.archived({ issueId: action.issueId, isShortTerm, completed });
      })
    )
  );

  updateTitle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.updateTitle),
      debounceTime(IssuesEffects.DEBOUNCE_TIME_MS),
      distinctUntilChanged((prev, curr) => prev.title === curr.title && prev.issueId === curr.issueId),
      filter(({ title }) => title.trim().length > 0),
      map(({ issueId, title }) => IssuesActions.updateIssue({ issueId, changes: { title } }))
    )
  );
  updateDescription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.updateDescription),
      debounceTime(IssuesEffects.DEBOUNCE_TIME_MS),
      distinctUntilChanged((prev, curr) => prev.description === curr.description && prev.issueId === curr.issueId),
      map(({ issueId, description }) => IssuesActions.updateIssue({ issueId, changes: { description } }))
    )
  );

  updateTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.updateTeam),
      concatLatestFrom(() => this.store.select(TeamSelectors.selectAllDictionary)),
      switchMap(([{ issueId, teamId }, teamsById]) =>
        this.issuesV2Service.updateIssue(issueId, { teamId }).pipe(
          // teamName keeps the All view's Team column truthful when the issue stays in the list
          map(() => IssuesActions.updateTeamSuccess({ issueId, teamId, teamName: teamsById[teamId]?.name })),
          catchError((error: unknown) => of(IssuesActions.updateTeamFailure({ error, issueId, teamId })))
        )
      )
    )
  );

  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.updateUser),
      switchMap(({ issueId, user }) =>
        this.issuesV2Service.updateIssue(issueId, { userId: user._id }).pipe(
          map(updatedIssue => IssuesActions.updateUserSuccess({ issue: updatedIssue, user })),
          catchError((error: unknown) => of(IssuesActions.updateUserFailure({ error, issueId, userId: user._id })))
        )
      )
    )
  );

  updateWho$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.updateWho),
      filter(({ who }) => who != null),
      concatLatestFrom(() => this.store.select(IssuesSelectors.selectSelectedIssue)),
      filter(([{ issueId, who }, issue]) => {
        // NOTE: on detail card open from list, an unnecessary event is triggered on open, so an unnecessary patch is sent,
        // refactoring looked complicated so I added a filter to prevent the unnecessary patch
        // updateWho action is not used in the reducer, the updateIssue action will update the state
        // using a reducer would update the value in the state before comparing the who values
        return issue?._id === issueId && who !== issue.who;
      }),
      debounceTime(IssuesEffects.DEBOUNCE_TIME_MS),
      map(([{ issueId, who }]) => IssuesActions.updateIssue({ issueId, changes: { who } }))
    )
  );

  // Handle both add and edit comment - they use the same backend operation
  addOrEditComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.addComment, IssuesActions.editComment),
      switchMap(({ issueId, comment }) =>
        this.issuesV2Service.updateComment(issueId, comment).pipe(
          map(() => IssuesActions.editCommentSuccess({ issueId, comment })),
          catchError(() => of(IssuesActions.editCommentFailure()))
        )
      )
    )
  );

  deleteComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.deleteComment),
      switchMap(({ issueId, comment }) =>
        this.issuesV2Service.deleteComment(issueId, comment).pipe(
          map(() => IssuesActions.deleteCommentSuccess({ issueId, comment })),
          catchError(() => of(IssuesActions.deleteCommentFailure()))
        )
      )
    )
  );

  addFollower$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.addFollower),
      switchMap(({ issueId, followerId }) =>
        this.issuesV2Service.addFollower(issueId, followerId).pipe(
          map(followerIds => IssuesActions.addFollowerSuccess({ issueId, followerIds })),
          catchError(() => of(IssuesActions.addFollowerFailure()))
        )
      )
    )
  );
  removeFollower$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.removeFollower),
      switchMap(({ issueId, followerId }) =>
        this.issuesV2Service.removeFollower(issueId, followerId).pipe(
          map(followerIds => IssuesActions.removeFollowerSuccess({ issueId, followerIds })),
          catchError(() => of(IssuesActions.removeFollowerFailure()))
        )
      )
    )
  );

  deleteIssue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.deleteIssue, IssuesActions.deleteIssueFromPB),
      switchMap(({ issueId }) => {
        return this.issuesV2Service.deleteIssue(issueId).pipe(
          map(() => IssuesActions.deleteIssueSuccess({ issueId })),
          catchError((error: unknown) => of(IssuesActions.deleteIssueFailure({ error, issueId })))
        );
      })
    )
  );

  confirmDeleteIssue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.deleteRequested),
      concatLatestFrom(({ issueId }) => [
        this.store.select(selectLanguageArea('issue')),
        this.store.select(IssuesSelectors.selectPlanningBoardPageView),
        this.store.select(IssuesSelectors.selectIssueById(issueId)),
        this.store.select(IssuesSelectors.selectIsShortTermIssues),
      ]),
      exhaustMap(([{ issueId }, language, isRocksPlanningBoardView, issue, isShortTermRoute]) => {
        const intervalCode = issue?.intervalCode ?? (isShortTermRoute ? IntervalCode.shortTerm : IntervalCode.longTerm);
        const interval =
          intervalCode === IntervalCode.shortTerm
            ? (language?.shortTerm ?? 'Short-Term')
            : (language?.longTerm ?? 'Long-Term');
        const issueItem = language?.item ?? 'Issue';

        return this.dialog
          .open<WarningConfirmDialogComponent, ConfirmDialogData>(WarningConfirmDialogComponent, {
            autoFocus: false,
            disableClose: true,
            width: '450px',
            maxWidth: 450,
            data: {
              title: `Delete ${interval} ${issueItem}`,
              message: `Are you sure you want to delete this ${interval} ${issueItem}?<br/><br/>
            This will move the ${interval} ${issueItem} and its associated content to Trash.`,
            },
          })
          .afterClosed()
          .pipe(
            filter((confirmed): confirmed is true => !!confirmed),
            map(() =>
              isRocksPlanningBoardView
                ? IssuesActions.deleteIssueFromPB({ issueId })
                : IssuesActions.deleteIssue({ issueId })
            )
          );
      })
    )
  );

  archiveIssue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.archiveIssue),
      switchMap(({ issueId, completed }) =>
        this.issuesV2Service.archiveIssue(issueId).pipe(
          map(() => IssuesActions.archiveIssueSuccess({ issueId, completed: completed ?? false })),
          catchError((error: unknown) => of(IssuesActions.archiveIssueFailure({ error, issueId })))
        )
      )
    )
  );

  unarchiveIssue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.unarchiveIssue),
      switchMap(({ issueId }) =>
        this.issuesV2Service.unarchiveIssue(issueId).pipe(
          map(() => IssuesActions.unarchiveIssueSuccess({ issueId })),
          catchError((error: unknown) => of(IssuesActions.unarchiveIssueFailure({ error, issueId })))
        )
      )
    )
  );

  setInterval$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.setIssueInterval),
      switchMap(({ issue, intervalCode }) =>
        this.issuesV2Service.setInterval(issue._id, intervalCode).pipe(
          map(updatedIssue => IssuesActions.setIssueIntervalSuccess({ issue: updatedIssue, intervalCode })),
          catchError((error: unknown) => of(IssuesActions.setIssueIntervalFailure({ error, issue, intervalCode })))
        )
      )
    )
  );

  syncArchiveFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterServiceActions.showArchived),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectTeamIssuesComponentLoaded),
        this.store.select(IssuesSelectors.selectArchiveFilter),
      ]),
      // Skip unchanged values: FilterService.setOptions() always resets the archive toggle,
      // so page init / toolbar reconfigure would otherwise trigger a redundant reload
      filter(([{ showArchived }, isLoaded, currentShowArchived]) => isLoaded && showArchived !== currentShowArchived),
      map(([{ showArchived }]) => IssuesActions.setArchiveFilter({ showArchived }))
    )
  );

  loadSentIssues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.loadSentIssues),
      concatLatestFrom(() => [
        this.filterService.selectedTeamId$,
        this.store.select(FilterUserSelectors.selectFilterBarUserIds),
      ]),
      // The API requires a concrete team id; skip when the filter bar is unset or holds the
      // 'all' transient during navigation (see README: Team filter and the "All Teams" race)
      filter(([, teamId]) => !!teamId && teamId !== teamAll._id),
      switchMap(([_, teamId, userIds]) => {
        const params = {
          teamId,
          intervalCode: IntervalCode.shortTerm,
          ...(userIds?.length && !userIds.includes('all') ? { userIds: userIds.filter(id => id !== 'all') } : {}),
        };

        return this.issuesV2Service.getSentIssues(params).pipe(
          map(response => IssuesActions.loadSentIssuesSuccess({ issues: response })),
          catchError((error: unknown) => of(IssuesActions.loadSentIssuesFailure({ error })))
        );
      })
    )
  );

  loadReceivedIssues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.loadReceivedIssues),
      concatLatestFrom(() => [
        this.filterService.selectedTeamId$,
        this.store.select(FilterUserSelectors.selectFilterBarUserIds),
      ]),
      // The API requires a concrete team id; skip when the filter bar is unset or holds the
      // 'all' transient during navigation (see README: Team filter and the "All Teams" race)
      filter(([, teamId]) => !!teamId && teamId !== teamAll._id),
      switchMap(([_, teamId, userIds]) => {
        const params = {
          workingTeamId: teamId,
          intervalCode: IntervalCode.shortTerm,
          ...(userIds?.length && !userIds.includes('all') ? { userIds: userIds.filter(id => id !== 'all') } : {}),
        };
        return this.issuesV2Service.getReceivedIssues(params).pipe(
          map(response => IssuesActions.loadReceivedIssuesSuccess({ issues: response })),
          catchError((error: unknown) => of(IssuesActions.loadReceivedIssuesFailure({ error })))
        );
      })
    )
  );

  loadStandaloneIssue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.loadStandaloneIssue),
      concatLatestFrom(() => this.store.select(IssuesSelectors.selectIsStandaloneIssueIdFromRoute)),
      filter(([, issueId]) => issueId !== null),
      switchMap(([_, issueId]) =>
        this.issuesV2Service.getIssue(issueId).pipe(
          map(issue => IssuesActions.loadStandaloneIssueSuccess({ issue })),
          catchError((error: unknown) => of(IssuesActions.loadStandaloneIssueFailure({ error })))
        )
      )
    )
  );

  syncTeamFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamListStateActions.getSelectedTeam),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectTeamIssuesComponentLoaded),
        this.store.select(selectFeatureFlag(FeatureFlagKeys.issuesAllTeamsFilter)),
      ]),
      // With the All-teams flag on, 'all' is a deliberate selection the search API accepts;
      // with it off, 'all' is only ever the navigation transient (see README) and is dropped
      filter(([{ id }, isLoaded, allTeamsEnabled]) => isLoaded && (allTeamsEnabled || id !== teamAll._id)),
      map(([{ id }]) => IssuesActions.setTeamFilter({ teamId: id }))
    )
  );

  closeDetailViewOnDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.deleteIssue, IssuesActions.deleteIssueFromPB),
      concatLatestFrom(() => this.store.select(IssuesSelectors.selectSelectedIssueId)),
      filter(([{ issueId }, selectedIssueId]) => issueId === selectedIssueId),
      map(() => IssuesActions.closeDetailView())
    )
  );

  closeDetailViewOnIntervalChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.setIssueIntervalSuccess),
      concatLatestFrom(() => this.store.select(IssuesSelectors.selectSelectedIssueId)),
      filter(([{ issue }, selectedIssueId]) => issue?._id === selectedIssueId),
      map(() => IssuesActions.closeDetailView())
    )
  );

  closeDetailViewOnArchive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.archiveIssue),
      concatLatestFrom(() => this.store.select(IssuesSelectors.selectSelectedIssueId)),
      filter(([{ issueId }, selectedIssueId]) => issueId === selectedIssueId),
      map(() => IssuesActions.closeDetailView())
    )
  );

  closeDetailViewOnUnarchive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.unarchiveIssue),
      concatLatestFrom(() => this.store.select(IssuesSelectors.selectSelectedIssueId)),
      filter(([{ issueId }, selectedIssueId]) => issueId === selectedIssueId),
      map(() => IssuesActions.closeDetailView())
    )
  );

  closeDetailViewOnFilterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.setTeamFilter, IssuesActions.setArchiveFilter, IssuesActions.setSearchFilter),
      concatLatestFrom(() => this.store.select(IssuesSelectors.selectSelectedIssueId)),
      filter(([, selectedIssueId]) => selectedIssueId !== null),
      map(() => IssuesActions.closeDetailView())
    )
  );

  closeDetailViewOnTeamUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.updateTeamSuccess),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectSelectedIssueId),
        this.store.select(IssuesSelectors.selectIsAllTeamsView),
      ]),
      // In the All view the issue stays in the list after a team change, so keep the detail open
      filter(([{ issueId }, selectedIssueId, isAllTeamsView]) => issueId === selectedIssueId && !isAllTeamsView),
      map(() => IssuesActions.closeDetailView())
    )
  );

  closeDetailViewOnSendToTeamSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        IssuesActions.sendToTeamSuccess,
        IssuesActions.sendBackToOriginalTeamSuccess,
        IssuesActions.recallIssueSuccess
      ),
      concatLatestFrom(() => this.store.select(IssuesSelectors.selectSelectedIssueId)),
      filter(([{ issue }, selectedIssueId]) => issue?._id === selectedIssueId),
      map(() => IssuesActions.closeDetailView())
    )
  );

  closeDetailViewOnStandaloneIssueFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        IssuesActions.setStandaloneIssueFailure,
        IssuesActions.loadLongTermIssuesForSltVto,
        IssuesActions.loadLongTermIssuesForTeamVto,
        IssuesActions.viewArchivedVto
      ),
      map(() => IssuesActions.closeDetailView())
    )
  );

  /**
   * Closes the detail view outlet when switching between short-term and long-term tabs.
   *
   * Why this is needed:
   * - The detail view is displayed in an auxiliary router outlet (side panel/overlay)
   * - When switching tabs, the currently selected issue may not exist or be relevant in the other tab
   * - Without closing the detail view, stale data from one tab could remain visible in the other tab
   * - This ensures a clean state and prevents showing incorrect issue details when switching tabs
   * - The detail view is tab-specific and should not persist across tab navigation
   */
  closeDetailOutletOnTabSwitch$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        concatLatestFrom(() => [this.store.select(selectIsInMeeting)]),
        //EXPLANATION: In meetings we have other tabs so we need to close the detail view a different way
        filter(([, isInMeeting]) => !isInMeeting),
        map(([action]: [RouterNavigationAction, boolean]) => {
          const url = action.payload.routerState.url;
          let tab: 'short-term' | 'long-term' | null = null;
          if (url.includes(`/${this.ISSUES_V2_ROUTE_PATH}/short-term`)) tab = 'short-term';
          if (url.includes(`/${this.ISSUES_V2_ROUTE_PATH}/long-term`)) tab = 'long-term';
          return { tab, hasDetail: url.includes('(detail:') };
        }),
        filter(({ tab }) => tab !== null),
        distinctUntilChanged((a, b) => a.tab === b.tab && a.hasDetail === b.hasDetail),
        pairwise(),
        // Tab must have changed AND the arriving URL must carry an open detail outlet.
        // curr.hasDetail === false means this is the intermediate close-detail navigation —
        // filtering it here (rather than before pairwise) keeps the buffer current.
        filter(([prev, curr]) => prev.tab !== curr.tab && curr.hasDetail),
        tap(([, curr]) => {
          this.auxiliaryRouterOutletService.closeDetailBeforeNavigate(this.activatedRoute, [
            this.ISSUES_V2_ROUTE_PATH,
            curr.tab,
          ]);
        })
      ),
    { dispatch: false }
  );

  // Navigate to issues/short-term when closing standalone issue card
  navigateOnStandaloneClose$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(IssuesActions.closeDetailView),
        concatLatestFrom(() => [this.store.select(IssuesSelectors.selectIsStandaloneIssueIdFromRoute)]),
        filter(([_, standaloneIssueId]) => !!standaloneIssueId),
        tap(() => {
          this.router.navigate(['issues', 'short-term']);
        })
      ),
    { dispatch: false }
  );

  // Close detail view for non-standalone cards
  closeDetailView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.closeDetailView),
      concatLatestFrom(() => [this.store.select(IssuesSelectors.selectIsStandaloneIssueIdFromRoute)]),
      filter(([_, standaloneIssueId]) => !standaloneIssueId),
      map(() => DetailViewActions.close())
    )
  );

  syncTeamFilterWhenTeamIssuesComponentLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.teamIssuesComponentLoaded),
      concatLatestFrom(() => [
        this.store.select(TeamSelectors.selectFilterBarTeamId),
        this.store.select(selectFeatureFlag(FeatureFlagKeys.issuesAllTeamsFilter)),
      ]),
      // Same rule as syncTeamFilter$: 'all' passes through only when the All-teams flag is on
      filter(([, teamId, allTeamsEnabled]) => !!teamId && (allTeamsEnabled || teamId !== teamAll._id)),
      map(([_, teamId]) => IssuesActions.setTeamFilter({ teamId }))
    )
  );

  /**
   * "All" is a temporary, Issues-only state — but it survives short/long-term tab switches
   * and the archive toggle. It resets when Trash opens (deleted issues are a separate,
   * team-scoped endpoint); Trash then reloads with the restored team via its teamChanged$
   * effect. Leaving the Issues section is handled by resetAllTeamsSelectionOnLeave$.
   */
  resetAllTeamsSelection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LayoutsIssuesEvents.showTrashItems),
      concatLatestFrom(() => [
        this.store.select(TeamSelectors.selectFilterBarTeamId),
        this.store.select(selectCurrentUser),
        this.store.select(CurrentUserSelectors.selectTeamIds),
      ]),
      filter(([, filterBarTeamId]) => filterBarTeamId === teamAll._id),
      // Same fallback order the platform uses: last accessed team, else first team, else none
      map(([, , user, teamIds]) =>
        TeamListStateActions.changeFilterBarTeam({ id: user?.lastAccessedTeamId ?? teamIds[0] ?? teamNone._id })
      )
    )
  );

  /**
   * Snap the global filter bar back to the user's last real team when navigation leaves
   * the Issues section (To-Dos, meetings, any other tool) while "All" is selected, so no
   * other page ever sees 'all'. Tab switches stay inside the section and keep the selection.
   */
  resetAllTeamsSelectionOnLeave$ = createEffect(() =>
    this.store.select(IssuesSelectors.selectIsIssuesSegmentInUrl).pipe(
      distinctUntilChanged(),
      pairwise(),
      filter(([wasInIssues, isInIssues]) => wasInIssues && !isInIssues),
      concatLatestFrom(() => [
        this.store.select(TeamSelectors.selectFilterBarTeamId),
        this.store.select(selectCurrentUser),
        this.store.select(CurrentUserSelectors.selectTeamIds),
      ]),
      filter(([, filterBarTeamId]) => filterBarTeamId === teamAll._id),
      map(([, , user, teamIds]) =>
        TeamListStateActions.changeFilterBarTeam({ id: user?.lastAccessedTeamId ?? teamIds[0] ?? teamNone._id })
      )
    )
  );

  /** All is not valid in meetings — snap back when the meeting route opens. */
  resetAllTeamsSelectionOnMeetingEnter$ = createEffect(() =>
    this.store.select(selectIsInMeeting).pipe(
      distinctUntilChanged(),
      pairwise(),
      filter(([wasInMeeting, isInMeeting]) => !wasInMeeting && isInMeeting),
      concatLatestFrom(() => [
        this.store.select(TeamSelectors.selectFilterBarTeamId),
        this.store.select(selectCurrentUser),
        this.store.select(CurrentUserSelectors.selectTeamIds),
      ]),
      filter(([, filterBarTeamId]) => filterBarTeamId === teamAll._id),
      map(([, , user, teamIds]) =>
        TeamListStateActions.changeFilterBarTeam({ id: user?.lastAccessedTeamId ?? teamIds[0] ?? teamNone._id })
      )
    )
  );

  /**
   * Leaving the All view for a real team: restore the persisted sort (or the ordinal default)
   * that the All view's forced team/priority sort replaced. Declared before
   * triggerLoadTeamIssues$ so the restored sort is in state before the reload fires.
   */
  restoreSortWhenLeavingAllTeams$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.setTeamFilter),
      pairwise(),
      filter(([previous, current]) => previous.teamId === teamAll._id && current.teamId !== teamAll._id),
      concatLatestFrom(() => this.store.select(IssuesSelectors.selectIsShortTermIssues)),
      map(([, isShortTerm]) => {
        const sort = this.issuesLocalStorageService.getSort(this.issuesLocalStorageService.getSortKey(isShortTerm));

        return sort
          ? IssuesActions.initializeSort({ field: sort.field, direction: sort.direction })
          : IssuesActions.initializeSort({ field: 'None' as IssueSortField, direction: SortDirection.ASC });
      })
    )
  );

  syncSearchTextFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterServiceActions.searchTextUpdated),
      concatLatestFrom(() => this.store.select(IssuesSelectors.selectTeamIssuesComponentLoaded)),
      filter(([, isLoaded]) => isLoaded),
      map(([{ searchText }]) => searchText),
      debounceTime(400),
      distinctUntilChanged(),
      map(searchText => IssuesActions.setSearchFilter({ searchText }))
    )
  );

  // Initialize page and pageSize from localStorage when component loads
  initializePaginationFromLocalStorage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.teamIssuesComponentLoaded),
      concatLatestFrom(() => this.store.select(IssuesSelectors.selectIsShortTermIssues)),
      map(([, isShortTerm]) => {
        const localStorageKey = `issues.${isShortTerm ? 'shortTerm' : 'longTerm'}`;
        const pageSize = getLocalPageSize(localStorageKey, PaginationConstants.PAGE_SIZES_WITH_200[1]);
        return IssuesActions.initializePagination({ page: 0, pageSize }); // Use initialize action
      })
    )
  );

  // Initialize sort from localStorage when component loads
  initializeSortFromLocalStorage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.teamIssuesComponentLoaded),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectIsShortTermIssues),
        this.store.select(IssuesSelectors.selectIsAllTeamsView),
      ]),
      // The All view has its own default (team sort, set by the setTeamFilter reducer);
      // don't overwrite it with the persisted single-team sort on tab switches
      filter(([, , isAllTeamsView]) => !isAllTeamsView),
      map(([, isShortTerm]) => {
        const sortKey = this.issuesLocalStorageService.getSortKey(isShortTerm);
        const sort = this.issuesLocalStorageService.getSort(sortKey);

        if (sort) {
          return IssuesActions.initializeSort({
            field: sort.field,
            direction: sort.direction,
          });
        }

        // No sort in localStorage or invalid, use defaults
        return IssuesActions.initializeSort({ field: 'None' as IssueSortField, direction: SortDirection.ASC });
      })
    )
  );

  // Initialize list view from user settings when component loads
  initializeListViewFromUserSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.teamIssuesComponentLoaded),
      concatLatestFrom(() => [
        this.store.select(selectCurrentUser),
        this.store.select(IssuesSelectors.selectIsShowingLongTermIssuesOnVto),
      ]),
      map(([, currentUser, isShowingLongTermIssuesOnVto]) => {
        // Force list view when in VTO route, otherwise use user settings
        const listView = isShowingLongTermIssuesOnVto
          ? IssuesListView.list
          : (currentUser?.settings?.issuesListView ?? IssuesListView.list);
        return IssuesActions.initializeListView({ listView });
      })
    )
  );

  //NOTE: if we want a different behavior in meetings we can filter out on some meeting routes
  resetFilterBarUsersOnTeamIssuesComponentLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.teamIssuesComponentLoaded),
      concatLatestFrom(() => this.store.select(TeamSelectors.selectFilterBarTeamId)),
      map(() => UserListStateActions.resetFilterBarUsers())
    )
  );

  // Force list view when navigating to VTO routes
  forceListViewOnVtoRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectIsShowingLongTermIssuesOnVto),
        this.store.select(IssuesSelectors.selectListView),
        this.store.select(IssuesSelectors.selectTeamIssuesComponentLoaded),
      ]),
      filter(
        ([, isShowingLongTermIssuesOnVto, currentListView, componentLoaded]) =>
          isShowingLongTermIssuesOnVto && componentLoaded && currentListView !== IssuesListView.list
      ),
      map(() => IssuesActions.setListView({ listView: IssuesListView.list }))
    )
  );

  savePaginationToLocalStorage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(IssuesActions.changePagination),
        concatLatestFrom(() => this.store.select(IssuesSelectors.selectIsShortTermIssues)),
        tap(([{ pageSize }, isShortTerm]) => {
          const key = this.issuesLocalStorageService.getPaginationKey(isShortTerm);
          this.issuesLocalStorageService.savePaginationSize(key, pageSize);
        })
      ),
    { dispatch: false }
  );

  // Save sort to localStorage when sort changes
  saveSortToLocalStorage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(IssuesActions.setSort),
        concatLatestFrom(() => [
          this.store.select(IssuesSelectors.selectIsShortTermIssues),
          this.store.select(IssuesSelectors.selectIsAllTeamsView),
        ]),
        // Sorting inside the transient All view must not clobber the persisted team-view sort
        filter(([, , isAllTeamsView]) => !isAllTeamsView),
        tap(([{ field, direction }, isShortTerm]) => {
          const sortKey = this.issuesLocalStorageService.getSortKey(isShortTerm);
          this.issuesLocalStorageService.saveSort(sortKey, field, direction);
        })
      ),
    { dispatch: false }
  );

  // Calculate next sort state when sortByField is dispatched
  calculateNextSortState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.changeSort),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectSortField),
        this.store.select(IssuesSelectors.selectSortDirection),
        this.store.select(selectIssueRatingUsesLabels),
      ]),
      map(([{ field }, currentSortField, currentSortDirection, issueRatingUsesLabels]) => {
        // A column that starts descending has to cycle DESC -> ASC -> None. The generic cycle below
        // reads "descending" as "you already clicked twice", so without this a descending-start
        // column would jump straight from its first click to no sort, and ascending would be
        // unreachable. Rating joins numOfLikes here only once a company has chosen a scale; without
        // one it still starts ascending, exactly as it always has.
        const startsDescending =
          field === 'numOfLikes' ||
          (field === 'rating' && ratingSortStartDirection(issueRatingUsesLabels) === SortDirection.DESC);

        // If clicking the same field, cycle through states
        if (currentSortField === field && currentSortField !== 'None') {
          if (startsDescending) {
            if (currentSortDirection === SortDirection.DESC) {
              return IssuesActions.setSort({ field, direction: SortDirection.ASC });
            } else {
              return IssuesActions.setSort({ field: 'None', direction: SortDirection.ASC });
            }
          }

          if (currentSortDirection === SortDirection.ASC) {
            return IssuesActions.setSort({ field, direction: SortDirection.DESC });
          } else {
            // DESC -> None
            return IssuesActions.setSort({ field: 'None', direction: SortDirection.ASC });
          }
        } else {
          // Clicking a different field or no field is sorted - start fresh.
          // numOfLikes starts with DESC (voted/highest first), so the most voted issue is at the top
          // the first time. Rating starts with DESC once a company has chosen a scale: the service
          // sorts a rank where 5 is always top, so descending means top priority first whichever end
          // that company calls top. A company with no scale keeps the ascending start it has today.
          const initialDirection = startsDescending ? SortDirection.DESC : SortDirection.ASC;
          return IssuesActions.setSort({ field: field, direction: initialDirection });
        }
      })
    )
  );

  triggerLoadReceivedIssues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        FilterServiceActions.refreshed,
        IssuesActions.setTeamFilter,
        IssuesActions.setArchiveFilter,
        UserListStateActions.changeFilterBarUsers
      ),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectTeamIssuesComponentLoaded),
        this.store.select(IssuesSelectors.selectIsShortTermIssues),
        this.store.select(IssuesSelectors.selectArchiveFilter),
      ]),
      filter(([, isLoaded, isShortTerm, showArchived]) => isLoaded && isShortTerm && !showArchived),
      map(() => IssuesActions.loadReceivedIssues())
    )
  );

  triggerLoadSentIssues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        FilterServiceActions.refreshed,
        IssuesActions.setTeamFilter,
        IssuesActions.setSearchFilter,
        IssuesActions.setArchiveFilter,
        IssuesActions.sendToTeamSuccess,
        UserListStateActions.changeFilterBarUsers
      ),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectTeamIssuesComponentLoaded),
        this.store.select(IssuesSelectors.selectIsShortTermIssues),
        this.store.select(IssuesSelectors.selectArchiveFilter),
      ]),
      filter(([, isLoaded, isShortTerm, showArchived]) => isLoaded && isShortTerm && !showArchived),
      map(() => IssuesActions.loadSentIssues())
    )
  );

  triggerLoadTeamIssuesOnFilterBarUsersChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserListStateActions.changeFilterBarUsers),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectTeamIssuesComponentLoaded),
        this.store.select(IssuesSelectors.selectIsIssuesSegmentInUrl),
      ]),
      filter(([, isLoaded, isIssuesSegmentInUrl]) => isLoaded && isIssuesSegmentInUrl),
      map(() => IssuesActions.loadTeamIssuesInBackground())
    )
  );

  refreshTeamIssuesOnMazToolUse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MazSharedActions.toolUse),
      filter(({ toolName }) => toolName === MazToolName.createIssue || toolName === MazToolName.editIssue),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectTeamIssuesComponentLoaded),
        this.store.select(IssuesSelectors.selectIsIssuesSegmentInUrl),
      ]),
      filter(([, isLoaded, isIssuesSegmentInUrl]) => isLoaded && isIssuesSegmentInUrl),
      delay(3000),
      map(() => IssuesActions.loadTeamIssuesInBackground())
    )
  );

  triggerLoadTeamIssues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        FilterServiceActions.refreshed,
        IssuesActions.setTeamFilter,
        IssuesActions.setArchiveFilter,
        IssuesActions.setSearchFilter,
        IssuesActions.loadLongTermIssuesForSltVto,
        IssuesActions.loadLongTermIssuesForTeamVto,
        IssuesActions.changePagination,
        IssuesActions.changeSort,
        IssuesActions.setSortFromBroadcast,
        IssuesActions.recallIssueSuccess
      ),
      concatLatestFrom(() => this.store.select(IssuesSelectors.selectTeamIssuesComponentLoaded)),
      filter(([, isLoaded]) => isLoaded),

      map(() => IssuesActions.loadTeamIssues())
    )
  );

  setIntervalFilterOnLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.loadTeamIssues),
      concatLatestFrom(() => this.store.select(IssuesSelectors.selectIntervalCode)),
      map(([, intervalCode]) => IssuesActions.setIntervalFilter({ intervalCode }))
    )
  );

  loadTeamIssues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.loadTeamIssues, IssuesActions.loadTeamIssuesInBackground),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectTeamFilter),
        this.store.select(IssuesSelectors.selectArchiveFilter),
        this.store.select(IssuesSelectors.selectIntervalCode),
        this.store.select(IssuesSelectors.selectSearchFilter),
        this.store.select(IssuesSelectors.selectOnlyPublicFilter),
        this.store.select(IssuesSelectors.selectPageIndex),
        this.store.select(IssuesSelectors.selectPageSize),
        this.store.select(IssuesSelectors.selectSortField),
        this.store.select(IssuesSelectors.selectSortDirection),
        this.store.select(FilterUserSelectors.selectFilterBarUserIds),
      ]),
      switchMap(
        ([
          ,
          teamId,
          showArchived,
          intervalCode,
          searchText,
          onlyPublic,
          page,
          pageSize,
          sortField,
          sortDirection,
          userIds,
        ]) => {
          const params: SearchIssuesParams = {
            teamId,
            intervalCode,
            archived: showArchived,
            searchText,
            page,
            pageSize,
            onlyPublic,
            // Only the active short-term list for one team queries the Sent list separately; every
            // other view must stay complete. isValid rather than `!== teamAll._id` because the store
            // initialises teamId to teamAll.name ('All'), not the id.
            ...(intervalCode === IntervalCode.shortTerm && ObjectId.isValid(teamId) && !showArchived
              ? { excludeSentToWorkingTeam: true }
              : {}),
          };

          if (sortField !== 'None') {
            params.sortField = sortField;
            params.sortDirection = sortDirection;
          }

          if (userIds?.length && !userIds.includes('all')) {
            params.userIds = userIds.filter(id => id !== 'all');
          }

          return this.issuesV2Service.searchTeamIssues(params).pipe(
            map(({ items, totalCount }) => IssuesActions.loadTeamIssuesSuccess({ issues: items, totalCount })),
            catchError((error: unknown) => of(IssuesActions.loadTeamIssuesFailure({ error })))
          );
        }
      )
    )
  );

  // Team Collaboration Effects
  openSendToTeamDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.openSendToTeamDialog),
      concatLatestFrom(() => [this.store.select(TeamSelectors.selectAll), this.store.select(selectCurrentUserId)]),
      switchMap(([{ issue }, allTeams, userId]) => {
        // selectAll is already name-sorted (entity adapter sortComparer). Just drop the owning
        // team — every other team the user can see (private included) is a valid target.
        const teams = allTeams.filter(team => team._id !== issue.teamId);

        return this.dialog
          .open<SendToTeamDialogComponent, SendToTeamDialogData, SendToTeamDialogResult>(SendToTeamDialogComponent, {
            data: { issue, teams },
            panelClass: 'medium-dialog',
            restoreFocus: false,
          })
          .afterClosed()
          .pipe(
            filter((result): result is SendToTeamDialogResult => !!result?.teamId),
            map(result => IssuesActions.sendToTeam({ issue, teamId: result.teamId, userId, comment: result.comment }))
          );
      })
    )
  );

  sendToTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.sendToTeam),
      concatLatestFrom(() => this.store.select(TeamSelectors.selectAllDictionary)),
      switchMap(([{ issue, teamId, userId, comment }, teams]) => {
        const originalTeamName = teams[issue.teamId]?.name || 'the team';
        const toTeamName = teams[teamId]?.name || 'the team';

        const comments: IssueComment[] = [
          ...(issue.comments ?? ([] as IssueComment[])),
          new IssueComment(userId, `${originalTeamName} sent the issue to ${toTeamName}`) as IssueComment,
        ];

        const trimmedComment = comment?.trim();
        if (trimmedComment) {
          comments.push(new IssueComment(userId, trimmedComment) as IssueComment);
        }

        const changes: Partial<Issue> = { comments, workingTeamId: teamId };

        return this.issuesV2Service.updateIssue(issue._id, changes).pipe(
          map(updatedIssue => IssuesActions.sendToTeamSuccess({ issue: updatedIssue, toTeamName })),
          catchError((error: unknown) => of(IssuesActions.sendToTeamFailure({ error, issueId: issue._id, toTeamName })))
        );
      })
    )
  );

  notifyOnSendToTeamSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.sendToTeamSuccess),
      map(({ toTeamName }) => NotificationActions.notify({ message: `Sent to ${toTeamName ?? 'the team'}` }))
    )
  );

  notifyOnSendToTeamFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.sendToTeamFailure),
      map(({ error, toTeamName }) =>
        NotificationActions.notifyError({ message: `Failed to send to ${toTeamName ?? 'the team'}`, error })
      )
    )
  );

  confirmOpenInDetailView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.openInDetailViewRequested),
      exhaustMap(({ issue }) =>
        this.discardChangesFlow.confirm().pipe(
          filter(Boolean),
          map(() => IssuesActions.openInDetailView({ issue }))
        )
      )
    )
  );

  openIssueInDetailView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.openInDetailView),
      concatLatestFrom(() => [this.store.select(IssuesSelectors.selectIsShortTermIssues)]),
      map(([{ issue }]) =>
        DetailViewActions.opened({
          config: {
            pathParts: [DetailType.issue, issue._id],
            queryParams: {
              // isShortTerm,// Let's see if we can make it work without this for issues card v2
            },
          },
          source: 'Issue V2 Selected',
        })
      )
    )
  );

  sendBackToOriginalTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.sendBackToOriginalTeam),
      concatLatestFrom(() => [
        this.store.select(TeamSelectors.selectAllDictionary),
        this.store.select(selectCurrentUserId),
      ]),
      switchMap(([{ issue }, teams, currentUserId]) => {
        const workingTeamName = teams[issue.workingTeamId]?.name || 'the team';
        const originalTeamName = issue.team?.name || 'the team';
        const changes: Partial<Issue> = {
          comments: [
            ...(issue.comments ?? []),
            new IssueComment(currentUserId, `${workingTeamName} sent the issue back to ${originalTeamName}`),
          ],
          workingTeamId: null,
        };

        return this.issuesV2Service.updateIssue(issue._id, changes).pipe(
          map(_ => IssuesActions.sendBackToOriginalTeamSuccess({ issue })),
          //Note: sending the original issue before update to be compatible with issues v1
          //I guess we might want to know the working team id when we receive the msg
          // NEXT: after issues v1 is deprecated, we can remove the original issue from the action
          catchError((error: unknown) => of(IssuesActions.sendBackToOriginalTeamFailure({ error, issue })))
        );
      })
    )
  );

  recallIssue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.recallIssue),
      concatLatestFrom(() => [
        this.store.select(TeamSelectors.selectAllDictionary),
        this.store.select(selectCurrentUserId),
      ]),
      switchMap(([{ issue }, teams, currentUserId]) => {
        const receivingTeamName = teams[issue.workingTeamId]?.name || 'the team';
        const sendingTeamName = issue.team?.name || teams[issue.teamId]?.name || 'the team';
        const changes: Partial<Issue> = {
          comments: [
            ...(issue.comments ?? []),
            new IssueComment(currentUserId, `Issue recalled from ${receivingTeamName} by ${sendingTeamName}`),
          ],
          workingTeamId: null,
          recalled: true,
        };

        return this.issuesV2Service.updateIssue(issue._id, changes).pipe(
          map(_ => IssuesActions.recallIssueSuccess({ issue })),
          catchError((error: unknown) => of(IssuesActions.recallIssueFailure({ error, issue })))
        );
      })
    )
  );

  notifyOnRecallSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.recallIssueSuccess),
      map(() => NotificationActions.notify({ message: 'Issue recalled' }))
    )
  );

  notifyOnRecallFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.recallIssueFailure),
      map(({ error }) => NotificationActions.notifyError({ message: 'Failed to recall issue', error }))
    )
  );

  dropIssue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.dropIssue),
      concatLatestFrom(() => this.store.select(IssuesSelectors.selectTeamIssuesColumns)),
      map(([{ issueId, previousIndex, currentIndex, sameColumn }, teamIssuesColumns]) => {
        //find in which column the issue is, we only support 2 columns for now
        const columnIndex = teamIssuesColumns.findIndex(column => column.some(issue => issue._id === issueId));

        //calculate the previous and current index taking into account the column length
        //NOTE: Pagination will be handled inside the moveIssue effect
        let effectivePreviousIndex = 0;
        let effectiveCurrentIndex = 0;

        if (sameColumn && columnIndex === 0) {
          effectivePreviousIndex = previousIndex;
          effectiveCurrentIndex = currentIndex;
        } else if (sameColumn && columnIndex === 1) {
          effectivePreviousIndex = previousIndex + teamIssuesColumns[0].length;
          effectiveCurrentIndex = currentIndex + teamIssuesColumns[0].length;
        } else if (!sameColumn && columnIndex === 0) {
          effectivePreviousIndex = previousIndex;
          effectiveCurrentIndex = currentIndex + teamIssuesColumns[0].length;
        } else if (!sameColumn && columnIndex === 1) {
          effectivePreviousIndex = previousIndex + teamIssuesColumns[0].length;
          effectiveCurrentIndex = currentIndex;
        }

        //NOTE: Pagination will be handled inside the moveIssue effect
        return IssuesActions.moveIssue({ fromIndex: effectivePreviousIndex, toIndex: effectiveCurrentIndex });
      })
    )
  );

  moveTeamIssue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.moveIssue),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectIsShortTermIssues),
        this.store.select(IssuesSelectors.selectTeamFilter),
        this.store.select(IssuesSelectors.selectSorting),
        this.store.select(IssuesSelectors.selectPagination),
        this.store.select(IssuesSelectors.selectAllIssues),
      ]),
      switchMap(
        ([
          { fromIndex, toIndex },
          isShortTerm,
          teamId,
          { sortField, sortDirection },
          { pageIndex, pageSize },
          allIssues,
        ]) => {
          const offset = pageIndex * pageSize;
          const startIndex = Math.min(fromIndex, toIndex);
          const endIndex = Math.max(fromIndex, toIndex);
          const issuesToUpdate = allIssues.slice(startIndex, endIndex + 1);

          const ordinalUpdates = issuesToUpdate.map((issue, i) => {
            return new OrdinalOrUserOrdinalUpdate(issue._id, startIndex + i + offset, 'ordinal');
          });

          return this.issuesV2Service
            .updateOrdinals(
              ordinalUpdates,
              teamId,
              isShortTerm ? IntervalCode.shortTerm : IntervalCode.longTerm,
              sortField,
              sortDirection ?? SortDirection.ASC
            )
            .pipe(
              map(() =>
                IssuesActions.moveIssueSuccess({
                  currentIndex: toIndex,
                  previousIndex: fromIndex,
                  offset,
                  isShortTerm,
                  currentTeamId: teamId,
                })
              ),
              catchError((error: unknown) => of(IssuesActions.moveIssueFailure({ error })))
            );
        }
      )
    )
  );

  archiveIssueFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.archiveIssueFailure),
      concatLatestFrom(() => this.store.select(selectLanguageArea('issue'))),
      map(([{ error }, language]) =>
        NotificationActions.notifyError({ message: `Failed to archive ${language.item}`, error })
      )
    )
  );

  unarchiveIssueFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.unarchiveIssueFailure),
      concatLatestFrom(() => this.store.select(selectLanguageArea('issue'))),
      map(([{ error }, language]) =>
        NotificationActions.notifyError({ message: `Failed to unarchive ${language.item}`, error })
      )
    )
  );

  moveIssueFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.moveIssueFailure),
      concatLatestFrom(() => this.store.select(selectLanguageArea('issue'))),
      map(([{ error }, language]) =>
        NotificationActions.notifyError({ message: `Failed to move ${language.item}`, error })
      )
    )
  );

  clearSortOnMoveIssue$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(IssuesActions.moveIssue, IssuesActions.setReorderedIssues),
        concatLatestFrom(() => this.store.select(IssuesSelectors.selectIsShortTermIssues)),
        tap(([, isShortTerm]) => {
          const sortKey = this.issuesLocalStorageService.getSortKey(isShortTerm);
          this.issuesLocalStorageService.deleteSort(sortKey);
        })
      ),
    { dispatch: false }
  );

  updateOrdinalsOnTopThreeSelection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.updateOrdinalsOnTopThreeSelection),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectTeamFilter),
        this.store.select(IssuesSelectors.selectIntervalCode),
        this.store.select(IssuesSelectors.selectSortField),
        this.store.select(IssuesSelectors.selectSortDirection),
      ]),
      switchMap(([{ ordinalUpdates }, teamId, intervalCode, sortField, sortDirection]) => {
        // Ensure direction is ASC when direction is null,
        // looks like ordinals on Issues service does not work if direction is null
        const direction = sortDirection || SortDirection.ASC;
        return this.issuesV2Service.updateOrdinals(ordinalUpdates, teamId, intervalCode, sortField, direction).pipe(
          map(() => IssuesActions.updateOrdinalsOnTopThreeSelectionSuccess()),
          catchError((error: unknown) => of(IssuesActions.updateOrdinalsOnTopThreeSelectionFailure({ error })))
        );
      })
    )
  );

  // Merge Issues Effect
  mergeIssues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.mergeIssues),
      switchMap(({ sourceIssue, targetIssue }) => {
        // Show confirmation dialog
        const confirmDialogRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogData>(ConfirmDialogComponent, {
          data: {
            title: 'Merge these two issues?',
            message: `This will append all data from ${sourceIssue.title} to ${targetIssue.title}`,
            confirmButtonText: 'Yes, Merge',
          },
        });

        return confirmDialogRef.afterClosed().pipe(
          switchMap((confirmed: boolean) => {
            // If user cancelled or closed dialog, clear merge state
            if (!confirmed) {
              return of(IssuesActions.cancelMerge());
            }
            // Prepare merged data
            const mergedTitle = `${targetIssue.title} / ${sourceIssue.title}`;
            const mergedDescription = `${targetIssue.description || ''}/<br>${sourceIssue.description || ''}`;
            const mergedComments = [...(targetIssue.comments ?? []), ...(sourceIssue.comments ?? [])];
            const mergedAttachments = [...(targetIssue.attachments ?? []), ...(sourceIssue.attachments ?? [])];

            // Create both API calls. Skip the source-complete PATCH when the source is already
            // completed — it would be a no-op the backend can't track, leaving no meaningful history
            // row. Pass the existing issue through so mergeIssuesSuccess still gets both sides.
            const completeSourceIssue$ = sourceIssue.completed
              ? of(sourceIssue)
              : this.issuesV2Service.updateIssue(sourceIssue._id, {
                  completed: true,
                  viaMerge: true,
                });
            const updateTargetIssue$ = this.issuesV2Service.updateIssue(targetIssue._id, {
              title: mergedTitle,
              description: mergedDescription,
              comments: mergedComments,
              attachments: mergedAttachments,
              viaMerge: true,
            });

            // Execute both calls in parallel using forkJoin to get both results
            return forkJoin({
              completedSourceIssue: completeSourceIssue$,
              updatedTargetIssue: updateTargetIssue$,
            }).pipe(
              map(({ completedSourceIssue, updatedTargetIssue }) =>
                IssuesActions.mergeIssuesSuccess({
                  completedIssue: completedSourceIssue,
                  mergedIssue: updatedTargetIssue,
                })
              ),
              catchError((error: unknown) => of(IssuesActions.mergeIssuesFailure({ error, sourceIssue, targetIssue })))
            );
          })
        );
      })
    )
  );

  notifyOnMergeSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.mergeIssuesSuccess),
      concatLatestFrom(() => this.store.select(selectLanguageArea('issue'))),
      map(([, language]) => NotificationActions.notify({ message: `${language.items} merged` }))
    )
  );

  // Handle merge target selection
  selectMergeTarget$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.selectMergeTarget),
      concatLatestFrom(() => this.store.select(IssuesSelectors.selectIssueToMerge)),
      filter(([{ targetIssue }, sourceIssue]) => {
        // Don't allow self-merging
        return !!sourceIssue && sourceIssue._id !== targetIssue._id;
      }),
      map(([{ targetIssue }, sourceIssue]) =>
        IssuesActions.mergeIssues({
          sourceIssue,
          targetIssue,
        })
      )
    )
  );

  // Voting Effects
  enableVoting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.enableVoting),
      map(() => MeetingIssuesEvents.toggleVotingEnabled({ votingEnabled: true }))
    )
  );

  disableVoting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.disableVoting),
      map(() => MeetingIssuesEvents.toggleVotingEnabled({ votingEnabled: false }))
    )
  );

  triggerLoadUserVotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        IssuesActions.teamIssuesComponentLoaded,
        IssuesActions.setIntervalFilter,
        MeetingIssuesEvents.toggleVotingEnabled
      ),
      concatLatestFrom(() => [
        this.store.select(selectIsInMeeting),
        this.store.select(IssuesSelectors.selectTeamFilter),
        this.store.select(IssuesSelectors.selectCurrentMeetingTeamId),
        this.store.select(IssuesSelectors.selectArchiveFilter),
        this.store.select(IssuesSelectors.selectIntervalCode),
        this.store.select(IssuesSelectors.selectCurrentMeetingVotingEnabled),
      ]),
      filter(([action, isInMeeting, teamId, currentMeetingTeamId, showArchived, intervalCode, votingEnabled]) => {
        // When the presenter enables voting mid-meeting, the store hasn't updated yet — use the action payload
        const isVotingEnabled = 'votingEnabled' in action ? action.votingEnabled : votingEnabled;
        return (
          !!isInMeeting &&
          !!teamId &&
          !!intervalCode &&
          teamId === currentMeetingTeamId &&
          !showArchived &&
          isVotingEnabled
        );
      }),
      map(([, , teamId, , , intervalCode]) => IssuesActions.loadUserVotes({ teamId, intervalCode }))
    )
  );

  loadUserVotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.loadUserVotes),
      // PubNub replays stored messages on subscribe, potentially dispatching loadUserVotes multiple times on join.
      // debounceTime collapses the burst, keeping only the last emission before making the HTTP call.
      debounceTime(50),
      switchMap(({ teamId, intervalCode }) =>
        this.issuesV2Service.getUserVotes(teamId, intervalCode).pipe(
          map(summary => IssuesActions.loadUserVotesSuccess({ userId: summary.userId, issueIds: summary.issueIds })),
          catchError((error: unknown) => of(IssuesActions.loadUserVotesFailure({ error })))
        )
      )
    )
  );

  addVote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.addVote),
      concatMap(({ issue, userId }) =>
        this.issuesV2Service.addVote(issue._id).pipe(
          map(issue => IssuesActions.addVoteSuccess({ issue, userId })),
          catchError((error: unknown) => of(IssuesActions.addVoteFailure({ error, issueId: issue._id })))
        )
      )
    )
  );

  removeVote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.removeVote),
      concatMap(({ issue, userId }) =>
        this.issuesV2Service.removeVote(issue._id).pipe(
          map(issue => IssuesActions.removeVoteSuccess({ issue, userId })),
          catchError((error: unknown) => of(IssuesActions.removeVoteFailure({ error, issueId: issue._id })))
        )
      )
    )
  );

  // Handle issue selection change - check if in merge mode
  issueSelectionChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.issueSelectionChanged),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectIssueToMerge),
        this.store.select(IssuesSelectors.selectIsSelectingTopThree),
      ]),
      filter(([{ issue }]) => {
        // Skip if it's the temporary inline issue
        return issue._id !== INLINE_TEMP_ISSUE_ID;
      }),
      switchMap(([{ issue }, issueToMerge, isSelectingTopThree]) => {
        // If we're in top three selection mode, don't open detail view
        // The component will handle dispatching selectTopThreeIssues when 3 are selected
        if (isSelectingTopThree) {
          return of();
        }

        // If we're in merge mode and clicking a different issue
        if (issueToMerge && issue._id !== issueToMerge._id) {
          // Dispatch selectMergeTarget - this will trigger the merge dialog
          return of(IssuesActions.selectMergeTarget({ targetIssue: issue }));
        }

        // Normal flow: open detail view
        return of(IssuesActions.openInDetailViewRequested({ issue }));
      })
    )
  );

  selectTopThreeIssues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.selectTopThreeIssues),
      concatLatestFrom(() => [this.store.select(IssuesSelectors.selectAllIssues)]),
      switchMap(([{ issues: selectedIssues }, allIssues]) => {
        // Get IDs of selected issues for easy lookup
        const selectedIssueIds = new Set(selectedIssues.map(issue => issue._id));
        const maxIndex = Math.max(...selectedIssues.map(issue => issue.ordinal ?? 0));

        // Build new array: selected issues first (at positions 0, 1, 2), then all others
        //NOTE: top 3 only works on the first page, old behavior was not working anyways on secondary pages
        const reorderedIssues = [
          ...selectedIssues, // The 3 selected issues at positions 0, 1, 2
          ...allIssues.filter(issue => !selectedIssueIds.has(issue._id)), // All other issues in their original order
        ].map((issue, index) => ({
          ...issue,
          ordinal: index, // Reset ordinals to 0, 1, 2, 3, ... based on new position
        }));

        // Create ordinal updates array for issues from 0 to maxIndex
        const ordinalUpdates = reorderedIssues
          .slice(0, maxIndex + 1)
          .map((issue, index) => new OrdinalOrUserOrdinalUpdate(issue._id, index, 'ordinal'));

        // Update state with reordered issues (optimistic update)
        return of(
          IssuesActions.setReorderedIssues({ issues: reorderedIssues }),
          IssuesActions.selectTopThreeIssuesSuccess({ issues: selectedIssues }),
          IssuesActions.updateOrdinalsOnTopThreeSelection({ ordinalUpdates }),
          IssuesActions.toggleTopThreeSelection()
        );
      })
    )
  );

  startSpinner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.loadTeamIssues),
      map(() => SpinnerActions.startPrimary({ source: 'IssuesEffects' }))
    )
  );

  stopSpinner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.loadTeamIssuesSuccess, IssuesActions.loadTeamIssuesFailure),
      map(() => SpinnerActions.stopPrimary({ source: 'IssuesEffects' }))
    )
  );

  // Centralized error handling for team issues
  handleTeamIssuesError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.loadTeamIssuesFailure),
      concatLatestFrom(() => this.store.select(selectLanguageArea('issue'))),
      map(([{ error }, language]) =>
        NotificationActions.notifyError({
          message: `Failed to load team ${language.items}. Please try again.`,
          error,
        })
      )
    )
  );

  handleLoadUserVotesError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.loadUserVotesFailure),
      map(({ error }) =>
        NotificationActions.notifyError({
          message: 'Failed to load your votes. Vote counts may be inaccurate.',
          error,
        })
      )
    )
  );

  handleLoadSentIssuesError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.loadSentIssuesFailure),
      concatLatestFrom(() => this.store.select(selectLanguageArea('issue'))),
      map(([{ error }, language]) =>
        NotificationActions.notifyError({
          message: `Failed to load sent ${language.items}. Please try again.`,
          error,
        })
      )
    )
  );

  handleLoadReceivedIssuesError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.loadReceivedIssuesFailure),
      concatLatestFrom(() => this.store.select(selectLanguageArea('issue'))),
      map(([{ error }, language]) =>
        NotificationActions.notifyError({
          message: `Failed to load received ${language.items}. Please try again.`,
          error,
        })
      )
    )
  );

  printPdf$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterServiceActions.printPdf),
      filter(({ url }) => {
        const allowedPrintPaths = ['issues', 'short-term', 'long-term'];
        return allowedPrintPaths.some(allowed => url.toLowerCase().includes(allowed));
      }),
      concatLatestFrom(() => this.store.select(selectShowIssuesTrash)),
      filter(([, showTrash]) => !showTrash),
      switchMap(() => this.dialog.open(PrintDialogComponent).afterClosed()),
      filter(printOptions => !!printOptions),
      concatLatestFrom(() => [this.store.select(IssuesSelectors.selectPrintPdfParams)]),
      switchMap(([printOptions, printParams]) =>
        this.printService
          .openPdf(PrintApi.issues, {
            ...printParams,
            printOptions,
          })
          .pipe(
            // openPdf resolves a string (and shows its own error toast) on failure; only
            // an ArrayBuffer means the PDF actually rendered.
            filter((result): result is ArrayBuffer => result instanceof ArrayBuffer),
            map(() => NotificationActions.notify({ message: 'PDF printed successfully' })),
            catchError((error: unknown) =>
              of(NotificationActions.notifyError({ message: 'Failed to print PDF', error }))
            )
          )
      )
    )
  );

  downloadExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterServiceActions.exportToExcel),
      filter(({ exportType }) => exportType === ExcelExportType.issues),
      filter(({ url }) => {
        const allowedPaths = ['issues', 'short-term', 'long-term'];
        return allowedPaths.some(allowed => url.toLowerCase().includes(allowed));
      }),
      concatLatestFrom(() => [
        this.store.select(selectShowIssuesTrash),
        this.store.select(IssuesSelectors.selectTeamIssuesComponentLoaded),
        this.store.select(IssuesSelectors.selectExcelExportParams),
        this.store.select(TeamSelectors.selectFilterBarTeam),
        this.store.select(selectLanguageArea('issue')),
      ]),
      // Deleted issues (Trash) support print only — no Excel export.
      filter(([, showTrash, componentLoaded]) => !showTrash && !!componentLoaded),
      switchMap(([, , , liveParams, team, language]) => {
        return this.issuesV2Service.downloadExcel(liveParams).pipe(
          tap(response => {
            const currentDate = new Date();
            const teamName = this.fileService.sanitizeFilename(team.name);
            const sti = this.fileService.sanitizeFilename(language.shortTerm);
            const lti = this.fileService.sanitizeFilename(language.longTerm);
            const fileName = `${sti}_${lti}_${teamName}_${formatDate(currentDate, 'MMddyyyy', 'en-US')}.xlsx`;
            this.fileService.downloadExcelFile(response, fileName);
          }),
          map(() => NotificationActions.notify({ message: 'Excel file downloaded successfully' })),
          catchError((error: unknown) =>
            of(NotificationActions.notifyError({ message: 'Failed to download Excel file', error }))
          )
        );
      })
    )
  );

  // Handle archive all completed button click
  archiveAllCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterServiceActions.archiveCompleted),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectTeamIssuesComponentLoaded),
        this.store.select(IssuesSelectors.selectTeamFilter),
        this.store.select(selectLanguageArea('issue')),
      ]),
      filter(([, componentLoaded]) => !!componentLoaded),
      switchMap(([, , teamId, issueLanguage]) =>
        this.issuesV2Service.archiveAllCompleted(teamId, issueLanguage).pipe(
          filter((numOfArchivedIssues): numOfArchivedIssues is number => numOfArchivedIssues !== null),
          switchMap(numOfArchivedIssues => {
            if (numOfArchivedIssues === 0) {
              return [
                NotificationActions.notify({
                  message: `No ${issueLanguage.items} were archived.`,
                  duration: 3000,
                }),
              ];
            }
            // eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
            return [
              IssuesActions.loadTeamIssues(),
              NotificationActions.notify({
                message: `${numOfArchivedIssues} ${
                  numOfArchivedIssues === 1 ? issueLanguage.item : issueLanguage.items
                } archived.`,
                duration: 3000,
              }),
            ];
          }),
          catchError((error: unknown) =>
            of(
              NotificationActions.notifyError({
                message: `Could not archive ${issueLanguage.items}. Please try again.`,
                error,
              })
            )
          )
        )
      )
    )
  );

  /** Flag on: the settings panel replaces the dialog. Rendered by `IssuesV2Component`. */
  openSettingsPanel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterServiceActions.openSettings),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectTeamIssuesComponentLoaded),
        this.store.select(IssuesSelectors.selectHasIssuesShortTermRouteData),
        this.store.select(selectFeatureFlag(FeatureFlagKeys.issuesSettingsPanel)),
      ]),
      filter(([, isLoaded, hasRouteData, panelEnabled]) => panelEnabled && isLoaded && hasRouteData),
      map(() =>
        DetailViewActions.opened({
          config: { pathParts: [DetailType.issuesSettings] },
          source: 'Issues Settings Opened',
        })
      )
    )
  );

  /** Team settings write, through the team-list effect that already owns the request. */
  saveTeamIssueSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.settingsPanelSaved),
      map(({ teamId, issueVoting, issuesSolveRate }) => ({
        teamId,
        update: {
          ...(issueVoting !== undefined ? { issueVoting } : {}),
          ...(issuesSolveRate !== undefined ? { issuesSolveRate } : {}),
        },
      })),
      filter(({ update }) => Object.keys(update).length > 0),
      map(({ teamId, update }) => TeamListStateActions.updateSettings({ teamId, update }))
    )
  );

  /** Re-emitted as a shared event so meeting effects can react without importing issues-v2. */
  notifyMeetingOfSettingsSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.settingsPanelSaved),
      map(({ teamId, issueVoting, issuesSolveRate, votesPerUser }) =>
        MeetingIssuesEvents.settingsSaved({ teamId, issueVoting, issuesSolveRate, votesPerUser })
      )
    )
  );

  /**
   * The panel's form is a snapshot of the team it opened on, but save() writes to whatever the
   * filter bar points at now. The dialog could not hit this because it was modal; the panel leaves
   * the filter bar usable, so a team switch has to close it or Team A's values land on Team B.
   *
   * `closeDetailViewOnFilterChange$` does not cover this — it only fires when an issue is selected.
   *
   * Archive is here for a different reason: settings do not apply to it, so both the tab (flagged)
   * and the old toggle hide the Settings button. An already-open panel has to go with it.
   */
  closeSettingsPanelOnContextChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.setTeamFilter, IssuesActions.setArchiveFilter, IssuesArchivePageActions.opened),
      concatLatestFrom(() => this.store.select(IssuesSelectors.selectIsIssuesSettingsPanelOpen)),
      filter(([, isPanelOpen]) => isPanelOpen),
      map(() => DetailViewActions.close())
    )
  );

  /** Closes as soon as the writes are dispatched, matching the dialog. */
  closeSettingsPanelOnSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.settingsPanelSaved),
      map(() => DetailViewActions.close())
    )
  );

  /** Pre-`issuesSettingsPanel` path. Deleted once the flag is fully rolled out. */
  openSettingsDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FilterServiceActions.openSettings),
        concatLatestFrom(() => [
          this.store.select(IssuesSelectors.selectTeamIssuesComponentLoaded),
          this.store.select(IssuesSelectors.selectHasIssuesShortTermRouteData),
          this.store.select(TeamSelectors.selectFilterBarTeam),
          this.store.select(IssuesSelectors.selectIsShortTermIssues),
          this.store.select(selectFeatureFlag(FeatureFlagKeys.issuesSettingsPanel)),
        ]),
        filter(([, isLoaded, hasRouteData, , , panelEnabled]) => !panelEnabled && isLoaded && hasRouteData),
        switchMap(([_, , , team, isShortTerm]) =>
          this.dialog
            .open(IssueListSettingsDialogComponent, {
              data: {
                issueVoting: team?.settings?.issueVoting ?? true,
                teamId: team?._id,
                issuesSolveRate: team?.settings?.issuesSolveRate,
                isShortTerm,
              },
              panelClass: 'medium-dialog',
              restoreFocus: false,
            })
            .afterClosed()
        ),
        filter(saved => !!saved)
      ),
    { dispatch: false }
  );

  /** Persists only the user's explicit toggle choice. Programmatic setListView dispatches
   *  (initializeListView hydration, forceListViewOnVtoRoute$) must not overwrite the saved
   *  preference — a VTO visit would otherwise silently reset a column-view user to list. */
  updateUserSettingsOnListViewChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.listViewToggleClicked),
      map(({ listView }) => UserSettingsActions.updateIssuesListView({ issuesListView: listView }))
    )
  );

  /** Bridges the user's toggle click to the state change. The click has its own action so
   *  analytics can track it separately from programmatic setListView dispatches. */
  setListViewOnToggleClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.listViewToggleClicked),
      map(({ listView }) => IssuesActions.setListView({ listView }))
    )
  );

  // Inline Create Effects
  openInlineCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.openInlineCreate),
      concatLatestFrom(() => [
        this.store.select(currentUserSelectors.selectCurrentUserId),
        this.store.select(selectCompanyId),
        this.store.select(TeamSelectors.selectFilterBarTeamId),
        this.store.select(IssuesSelectors.selectIntervalCode),
      ]),
      map(([_, userId, companyId, teamId, intervalCode]) => {
        const seededTeamId = teamId === teamAll._id ? null : teamId;
        const temporaryIssue: Issue = {
          _id: INLINE_TEMP_ISSUE_ID,
          userId,
          teamId: seededTeamId,
          companyId,
          title: '',
          description: '',
          intervalCode,
          ordinal: -1,
          completed: false,
          archived: false,
          deleted: false,
        };
        return IssuesActions.addInline({ issue: temporaryIssue });
      })
    )
  );

  saveInline$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.saveInline, IssuesActions.inlineTeamSelected, IssuesActions.inlineDraftTitleSaveRequested),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectInlineTempIssue),
        this.store.select(TeamSelectors.selectAllDictionary),
      ]),
      // Multiple draft events can request the same save; ignore further attempts
      // while a create is in flight.
      exhaustMap(([, temporaryIssue, teamsById]) => {
        const title = temporaryIssue?.title?.trim();
        const teamId = temporaryIssue?.teamId;

        // An incomplete draft is a normal interim state (e.g. outside click before a team is
        // chosen on Team All) — keep the row open and do nothing.
        if (!temporaryIssue || !title || !teamId || teamId === teamAll._id) {
          return of(appActions.noop());
        }

        // Strip the temporary ID before sending to API
        const { _id: _, ...noIdIssue } = { ...temporaryIssue, title };
        return this.issuesV2Service.createIssue(noIdIssue).pipe(
          map(createdIssue =>
            IssuesActions.saveInlineSuccess({
              // teamName keeps the All view's Team column truthful for the freshly created row
              issue: { ...createdIssue, teamName: createdIssue.teamName ?? teamsById[teamId]?.name },
            })
          ),
          catchError((error: unknown) => of(IssuesActions.saveInlineFailed({ error })))
        );
      })
    )
  );

  openCreateLinkedItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.openCreateLinkedItem),
      map(({ issue, itemType }) =>
        UniversalCreateActions.open({
          data: {
            item: issue as Item,
            itemType,
            createdFrom: {
              id: issue._id,
              type: LinkedItemTypeEnum.issue,
            },
          },
        })
      )
    )
  );

  allIssuesListsLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        IssuesActions.loadTeamIssuesSuccess,
        IssuesActions.loadTeamIssuesFailure,
        IssuesActions.loadSentIssuesSuccess,
        IssuesActions.loadSentIssuesFailure,
        IssuesActions.loadReceivedIssuesSuccess,
        IssuesActions.loadReceivedIssuesFailure
      ),

      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectIsShortTermIssues),
        this.store.select(IssuesSelectors.selectArchiveFilter),
        this.store.select(IssuesSelectors.selectLoading),
        this.store.select(IssuesSelectors.selectLoadingSent),
        this.store.select(IssuesSelectors.selectLoadingReceived),
      ]),
      filter(([_, isShortTerm, showArchived, loading, loadingSent, loadingReceived]) => {
        //NOTE: on short term we load all 3 lists, on long term or archived we only load the team list
        //this effect and filter is used to avoid multiple calls to the api when navigating to a detail route
        const isListLoadSettled = (value: LoadingState) =>
          value === LoadingState.Loaded || value === LoadingState.Error;

        if (!isShortTerm || showArchived) {
          return true;
        } else {
          return isListLoadSettled(loading) && isListLoadSettled(loadingSent) && isListLoadSettled(loadingReceived);
        }
      }),
      map(() => IssuesActions.allIssuesListsLoaded())
    )
  );

  // When the page is reloaded or we open a url with detail route, set the selected issue to the issue in the route
  setSelectedIssueOnReload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.allIssuesListsLoaded, IssuesActions.loadIssueInDetailView),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectIssueV2IdFromRoute),
        this.store.select(IssuesSelectors.selectIssuesEntitiesDict),
        this.store.select(IssuesSelectors.selectReceivedIssuesEntitiesDict),
        this.store.select(IssuesSelectors.selectSentIssuesEntitiesDict),
        this.store.select(IssuesSelectors.selectIsShortTermIssues),
        this.store.select(IssuesSelectors.selectSelectedIssueId),
      ]),
      filter(([_, routeIssueId]) => !!routeIssueId),
      filter(([_, routeIssueId, , , , , selectedIssueId]) => routeIssueId !== selectedIssueId),
      map(([_, routeIssueId, teamEntities, receivedEntities, sentEntities, isShortTerm]) => {
        const issue = isShortTerm
          ? teamEntities[routeIssueId] || receivedEntities[routeIssueId] || sentEntities[routeIssueId]
          : teamEntities[routeIssueId];

        return { issue, routeIssueId };
      }),
      switchMap(({ issue, routeIssueId }) => {
        if (!issue) {
          //get issue by id if not found in any lists
          return this.issuesV2Service.getIssue(routeIssueId).pipe(
            map(issue => IssuesActions.setStandaloneIssue({ issue })),
            catchError(() => of(IssuesActions.setStandaloneIssueFailure()))
          );
        }

        return of(IssuesActions.selectIssue({ issueId: issue._id }));
      })
    )
  );

  /**
   * Detects navigation to a detail route (auxiliary outlet).
   * Issues links in Notifications, that are on the same team, do not load the issue in the detail view,
   * we need to detect a route change, the detail service only opens the card but it does not load the issue.
   * I reuse setSelectedIssueOnReload$ in this case but we could duplicate the logic that loads the issue.
   */
  loadIssueInDetailViewIfNotOpened$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectIsIssuesRoute),
        this.store.select(IssuesSelectors.selectTeamIssuesComponentLoaded),
        this.store.select(IssuesSelectors.selectIssueV2IdFromRoute),
        this.store.select(IssuesSelectors.selectSelectedIssueId),
      ]),
      filter(
        ([, isIssuesRoute, componentLoaded, routeIssueId, selectedIssueId]) =>
          // Off the issues page (rocks, my-90, a deep link from a notification) the lists never load,
          // so waiting on the team-issues component would leave the card blank forever.
          !!routeIssueId && routeIssueId !== selectedIssueId && (!isIssuesRoute || componentLoaded)
      ),
      map(([, , , routeIssueId]) => IssuesActions.loadIssueInDetailView({ issueId: routeIssueId }))
    )
  );

  //VTO EFFECTS
  loadLeadershipVto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VtoStateActions.loadLeadershipVto),
      concatLatestFrom(() => [this.store.select(IssuesSelectors.selectIsShowingLongTermIssuesOnVto)]),
      filter(([, isShowingLongTermIssuesOnVto]) => isShowingLongTermIssuesOnVto),
      concatLatestFrom(() => [this.store.select(selectCompanySltTeamId)]),
      map(([, teamId]) => IssuesActions.loadLongTermIssuesForSltVto({ teamId }))
    )
  );

  loadTeamVto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VtoStateActions.loadTeamVto),
      concatLatestFrom(() => [this.store.select(IssuesSelectors.selectIsShowingLongTermIssuesOnVto)]),
      filter(([, isShowingLongTermIssuesOnVto]) => isShowingLongTermIssuesOnVto),
      concatLatestFrom(() => [this.store.select(TeamSelectors.selectFilterBarTeamId)]),
      map(([, teamId]) => IssuesActions.loadLongTermIssuesForTeamVto({ teamId }))
    )
  );

  viewArchivedVto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VtoStateActions.viewArchivedVto),
      map(({ archivedVto }) => IssuesActions.viewArchivedVto({ vto: archivedVto }))
    )
  );

  // Determine if metrics can be shown based on meeting state, team filter, and archive filter
  determineCanShowMetrics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.teamIssuesComponentLoaded, IssuesActions.setTeamFilter, IssuesActions.setArchiveFilter),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectTeamFilter),
        this.store.select(IssuesSelectors.selectArchiveFilter),
        this.store.select(IssuesSelectors.selectIsShortTermIssues),
        this.store.select(IssuesSelectors.selectCurrentMeeting),
      ]),
      map(([, teamId, showArchived, isShortTerm, currentMeeting]) => {
        const canShowMetrics =
          isShortTerm &&
          !!currentMeeting?.inProgress &&
          [MeetingType.weekly, MeetingType.custom].includes(currentMeeting.type) &&
          teamId === currentMeeting.teamId &&
          !showArchived;

        return IssuesActions.setCanShowMetrics({ canShowMetrics });
      })
    )
  );

  handleSendBackToOriginalTeamFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.sendBackToOriginalTeamFailure),
      map(({ error }) =>
        NotificationActions.notifyError({
          message: 'Failed to send issue back to original team. Please try again.',
          error,
        })
      )
    )
  );
}
